// ============================================================================
// Roof Designer (Stufe 1) · PropertiesPanel — Eingaben (helle Seite)
// ----------------------------------------------------------------------------
// Maßstab, Modul-Spec, Neigung/Azimut, Abstände, Hindernistyp. Keine Berechnung
// hier — nur Dispatch in den Reducer; abgeleitete Werte zeigt der ResultPanel.
// ============================================================================
import type { ObstacleType } from "@energyos/shared-types";
import type { DesignerState } from "../types.js";
import type { DesignerAction } from "../state.js";

interface Props {
  state: DesignerState;
  dispatch: (a: DesignerAction) => void;
}

const OBSTACLE_LABELS: Record<ObstacleType, string> = {
  chimney: "Kamin",
  dormer: "Gaube",
  skylight: "Dachfenster",
  vent: "Entlüfter",
  snow_guard: "Schneefang",
  parapet: "Attika",
  lightning_rod: "Blitzschutz",
  sat_dish: "SAT-Anlage",
  safety_anchor: "Absturzsicherung",
  tree: "Baum",
  neighbor_building: "Nachbargebäude",
  other: "Sonstiges",
};

const num = (v: string): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export function PropertiesPanel({ state, dispatch }: Props) {
  const cal = state.calibration;
  const calReady = cal.a !== null && cal.b !== null;

  return (
    <div className="rd-panel">
      <h3 className="rd-panel__title">Eigenschaften</h3>

      {/* ── Maßstab ─────────────────────────────────────────── */}
      <div className="rd-field">
        <span className="rd-field__label">Maßstab</span>
        <div style={{ fontSize: "var(--fs-xs)", color: "var(--color-text-muted)", marginBottom: 4 }}>
          {cal.confirmed
            ? `Gesetzt: ${(cal.metersPerPixel ?? 0).toFixed(4)} m/px`
            : calReady
              ? "Zwei Punkte gesetzt — Länge eintragen und bestätigen."
              : `Werkzeug "Maßstab" wählen und zwei Referenzpunkte klicken.`}
        </div>
        <div className="rd-row">
          <div className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">bekannte Länge (m)</span>
            <input
              className="rd-input"
              type="number"
              min={0.1}
              step={0.1}
              value={cal.realLengthM}
              onChange={(e) => dispatch({ type: "SET_CALIB_LENGTH", realLengthM: num(e.target.value) })}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
            <button
              className="rd-seg__btn rd-seg__btn--active"
              style={{ borderRadius: "var(--radius-sm)", flex: 1 }}
              onClick={() => dispatch({ type: "CONFIRM_CALIBRATION" })}
              disabled={!calReady}
            >
              Setzen
            </button>
          </div>
        </div>
        {(calReady || cal.confirmed) && (
          <button
            className="rd-seg__btn"
            style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border-strong)", marginTop: 6 }}
            onClick={() => dispatch({ type: "RESET_CALIBRATION" })}
          >
            Maßstab zurücksetzen
          </button>
        )}
      </div>

      {/* ── Modul ───────────────────────────────────────────── */}
      <div className="rd-field">
        <span className="rd-field__label">Modul</span>
        <div className="rd-row">
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Leistung (Wp)</span>
            <input
              className="rd-input"
              type="number"
              min={1}
              value={state.moduleSpec.wp}
              onChange={(e) => dispatch({ type: "SET_MODULE_SPEC", patch: { wp: num(e.target.value) } })}
            />
          </label>
          <div className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Ausrichtung</span>
            <div className="rd-seg" role="group" aria-label="Modul-Ausrichtung">
              <button
                className={`rd-seg__btn ${state.orientation === "portrait" ? "rd-seg__btn--active" : ""}`}
                onClick={() => dispatch({ type: "SET_ORIENTATION", orientation: "portrait" })}
              >
                Hochkant
              </button>
              <button
                className={`rd-seg__btn ${state.orientation === "landscape" ? "rd-seg__btn--active" : ""}`}
                onClick={() => dispatch({ type: "SET_ORIENTATION", orientation: "landscape" })}
              >
                Quer
              </button>
            </div>
          </div>
        </div>
        <div className="rd-row">
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Breite (m)</span>
            <input
              className="rd-input"
              type="number"
              min={0.1}
              step={0.01}
              value={state.moduleSpec.widthM}
              onChange={(e) => dispatch({ type: "SET_MODULE_SPEC", patch: { widthM: num(e.target.value) } })}
            />
          </label>
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Höhe (m)</span>
            <input
              className="rd-input"
              type="number"
              min={0.1}
              step={0.01}
              value={state.moduleSpec.heightM}
              onChange={(e) => dispatch({ type: "SET_MODULE_SPEC", patch: { heightM: num(e.target.value) } })}
            />
          </label>
        </div>
      </div>

      {/* ── Dachneigung / Azimut ────────────────────────────── */}
      <div className="rd-field">
        <span className="rd-field__label">Dachfläche</span>
        <div className="rd-row">
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Neigung (°)</span>
            <input
              className="rd-input"
              type="number"
              min={0}
              max={89}
              value={state.tiltDeg}
              onChange={(e) => dispatch({ type: "SET_TILT", tiltDeg: num(e.target.value) })}
            />
          </label>
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Azimut (° / 0=N, 180=S)</span>
            <input
              className="rd-input"
              type="number"
              min={0}
              max={360}
              value={state.azimuthDeg}
              onChange={(e) => dispatch({ type: "SET_AZIMUTH", azimuthDeg: num(e.target.value) })}
            />
          </label>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--fs-xs)", color: "var(--color-text-muted)" }}>
          <input
            type="checkbox"
            checked={state.tiltConfirmed}
            onChange={(e) => dispatch({ type: "SET_TILT_CONFIRMED", confirmed: e.target.checked })}
          />
          Neigung bestätigt (sonst als Annahme geführt)
        </label>
      </div>

      {/* ── Abstände ────────────────────────────────────────── */}
      <div className="rd-field">
        <span className="rd-field__label">Abstände (m)</span>
        <div className="rd-row">
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Dachrand</span>
            <input
              className="rd-input"
              type="number"
              min={0}
              step={0.05}
              value={state.edgeSetbackM}
              onChange={(e) => dispatch({ type: "SET_SETBACKS", edgeM: num(e.target.value) })}
            />
          </label>
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">um Hindernis</span>
            <input
              className="rd-input"
              type="number"
              min={0}
              step={0.05}
              value={state.obstacleSetbackM}
              onChange={(e) => dispatch({ type: "SET_SETBACKS", obstacleM: num(e.target.value) })}
            />
          </label>
        </div>
        <div className="rd-row">
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Modulspalt</span>
            <input
              className="rd-input"
              type="number"
              min={0}
              step={0.01}
              value={state.moduleGapM}
              onChange={(e) => dispatch({ type: "SET_GAPS", moduleGapM: num(e.target.value) })}
            />
          </label>
          <label className="rd-field" style={{ margin: 0 }}>
            <span className="rd-field__label">Reihenabstand</span>
            <input
              className="rd-input"
              type="number"
              min={0}
              step={0.05}
              value={state.rowGapM}
              onChange={(e) => dispatch({ type: "SET_GAPS", rowGapM: num(e.target.value) })}
            />
          </label>
        </div>
      </div>

      {/* ── Hindernis-Typ ───────────────────────────────────── */}
      <label className="rd-field" style={{ marginBottom: 0 }}>
        <span className="rd-field__label">Neues Hindernis als</span>
        <select
          className="rd-select"
          value={state.obstacleDraftType}
          onChange={(e) => dispatch({ type: "SET_OBSTACLE_DRAFT_TYPE", obstacleType: e.target.value as ObstacleType })}
        >
          {(Object.keys(OBSTACLE_LABELS) as ObstacleType[]).map((k) => (
            <option key={k} value={k}>
              {OBSTACLE_LABELS[k]}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
